import { Grid, Theme, useTheme, TextField, Checkbox, FormControlLabel, Box } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { AddPairingPositions } from "../Forms/CrewPositionsForm/AddPairingPositions";
import _ from "lodash";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { buildCreditDisplay, CreditSource, getDefaultPlannedCreditMap } from "@vmosolutions/oc-interfaces";
import type { ICalculatePlannedCreditResult, ICreditDisplay, IPlannedCreditMap } from "@vmosolutions/oc-interfaces";
import { CreditSummaryCard } from "./CreditSummaryCard";

interface IGeneralFieldsWrapperProps {
  id: string;
  /** True when the editor holds unsaved edits. Blocks the credit recalculation, which reads saved content. */
  pairingDirty: boolean;
  /** Applies the records a credit recalculation wrote. */
  onCreditRecalculated: (result: ICalculatePlannedCreditResult) => void | Promise<void>;
}

const GeneralFieldsWrapperStyle = (theme: Theme): any => ({
  "& .MuiInputBase-root": {
    border: "1px solid rgba(255, 255, 255, 0.23) !important",
    borderRadius: "0 !important",
    padding: 0,
    backgroundColor: theme.custom.forms.inputBack,
  },
  "& .isBiddable_checkbox": {
    width: "100px",
    margin: "0",
  },
  "& .plannedCredit_textfield": {
    width: "180px",
  },
  "& .crewnote": {
    margin: "0px 10px 10px 10px",
    width: "calc(100% - 20px)",
    "& .input_control": {
      "& .input-base": {
        backgroundColor: "#ffd351",
        "& textarea": { color: "black", fontWeight: 500 },
        "&:after": { borderBottom: "2px solid black" },
      },
      "& label": { width: "auto", color: "#ffd351" },
    },
  },
});

export const GeneralFieldsWrapper = (props: IGeneralFieldsWrapperProps): JSX.Element => {
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[props.id]);
  const [crewNotes, setCrewNotes] = useState<string>(storedPairing?.crewNote?.note ?? "");
  const dispatch = useDispatch();
  const theme: Theme = useTheme();
  const creditSettingValue = useSelector((state: IRootState) => state.credit);

  const defaultPlannedCreditMap = useMemo<IPlannedCreditMap | undefined>(() => {
    if (!creditSettingValue || !creditSettingValue.settings || creditSettingValue.settings.length < 1) {
      return undefined;
    }
    return creditSettingValue.settings.length > 0 ? getDefaultPlannedCreditMap(creditSettingValue.settings) : undefined;
  }, [creditSettingValue]);
  //merge default planned credit map with the stored pairing planned credit map
  const plannedCreditMap = useMemo<IPlannedCreditMap | undefined>(() => {
    //if there is no default planned credit map and no stored pairing planned credit map, return undefined
    //case where the customer is not using this setting at all
    if (!defaultPlannedCreditMap && !storedPairing?.plannedCreditMap) {
      return undefined;
    }
    const mergedMap: IPlannedCreditMap = { ...defaultPlannedCreditMap };
    //only neede to merge if stored pairing map exists
    if (storedPairing?.plannedCreditMap) {
      Object.keys(storedPairing.plannedCreditMap).forEach((key) => {
        mergedMap[key] = {
          credit: storedPairing.plannedCreditMap[key].credit,
          displayName: storedPairing.plannedCreditMap[key].displayName,
        };
      });
    }
    return mergedMap;
  }, [defaultPlannedCreditMap, storedPairing?.plannedCreditMap]);

  /**
   * Index of each module's position in creditSettingValue.settings - "the order the modules are in the
   * client settings" the operator actually configured, which is what the calculated rows should follow.
   * displayOrder is not reliably populated, so it isn't used as the sort key.
   */
  const settingOrder = useMemo<Map<string, number>>(() => {
    const order = new Map<string, number>();
    (creditSettingValue?.settings ?? []).forEach((setting, index) => order.set(setting.plannedCreditField, index));
    return order;
  }, [creditSettingValue?.settings]);

  const byModuleOrder = useCallback(
    (a: ICreditDisplay, b: ICreditDisplay): number =>
      (settingOrder.get(a.plannedCreditField) ?? Number.MAX_SAFE_INTEGER) -
      (settingOrder.get(b.plannedCreditField) ?? Number.MAX_SAFE_INTEGER),
    [settingOrder]
  );

  /**
   * The official Planned calculated credit, one row per module, in client-setting order.
   *
   * Read-only and additive: a customer with no calculated record simply gets no row, so this stays
   * invisible until a calculation has actually run for the pairing. Kept separate from Current so it can
   * render alongside RecalculateCreditButton, which only ever writes this side.
   */
  const plannedCreditRows = useMemo<ICreditDisplay[]>(() => {
    if (!storedPairing) {
      return [];
    }
    return buildCreditDisplay(storedPairing, creditSettingValue?.settings ?? [], CreditSource.planned).sort(
      byModuleOrder
    );
  }, [storedPairing, creditSettingValue?.settings, byModuleOrder]);

  /** The latest calculated credit for the pairing's current content, one row per module, same order. */
  const currentCreditRows = useMemo<ICreditDisplay[]>(() => {
    if (!storedPairing) {
      return [];
    }
    return buildCreditDisplay(storedPairing, creditSettingValue?.settings ?? [], CreditSource.current).sort(
      byModuleOrder
    );
  }, [storedPairing, creditSettingValue?.settings, byModuleOrder]);

  useEffect(() => {
    setCrewNotes(storedPairing?.crewNote?.note);
  }, [storedPairing?.crewNote?.note]);

  return (
    <>
      {storedPairing && (
        <Grid container direction="row" wrap="wrap" sx={() => GeneralFieldsWrapperStyle(theme)}>
          <Grid container alignItems="center">
            <Grid item>
              <FormControlLabel
                className="isBiddable_checkbox"
                control={
                  <Checkbox
                    size="small"
                    checked={storedPairing.isBiddable}
                    onChange={(e: any) => {
                      dispatch(updatePairingEditor(props.id, { isBiddable: e.target.checked }));
                    }}
                  />
                }
                label="Biddable"
                labelPlacement="start"
                sx={{ padding: "3px 3px 6px 3px" }}
                slotProps={{
                  typography: { fontSize: "12px !important" },
                }}
              />
            </Grid>
            {!creditSettingValue.hidePlannedCreditField && (
              <Grid item>
                <Box sx={{ padding: "3px 3px 6px 3px" }}>
                  <Grid container wrap="nowrap" alignItems="center">
                    <Box sx={{ fontSize: "12px", width: "90px", color: `${theme.custom.forms.inputText} !important` }}>
                      Planned Credit
                    </Box>
                    <TextField
                      className="credit_input"
                      type="number"
                      sx={{ width: "90px" }}
                      variant="outlined"
                      value={storedPairing?.plannedCredit}
                      fullWidth={false}
                      size="small"
                      onChange={(e: any) => {
                        dispatch(updatePairingEditor(props.id, { plannedCredit: parseFloat(e.target.value) }));
                      }}
                      inputProps={{
                        sx: {
                          padding: "2px 0 2px 5px",
                          fontSize: "12px",
                          zIndex: 2,
                          color: `${theme.custom.forms.inputText} !important`,
                        },
                      }}
                    />
                  </Grid>
                </Box>
              </Grid>
            )}
            {plannedCreditMap &&
              !_.isEmpty(plannedCreditMap) &&
              Object.keys(plannedCreditMap).map((key) => (
                <Grid item key={key}>
                  <Box sx={{ padding: "3px 3px 6px 3px" }}>
                    <Grid container wrap="nowrap" alignItems="center">
                      <Box
                        sx={{ fontSize: "12px", width: "90px", color: `${theme.custom.forms.inputText} !important` }}
                      >
                        {plannedCreditMap[key]?.displayName ?? key}
                      </Box>
                      <TextField
                        className="credit_input"
                        type="number"
                        sx={{ width: "90px" }}
                        variant="outlined"
                        value={plannedCreditMap[key].credit}
                        fullWidth={false}
                        size="small"
                        onChange={(e: any) => {
                          dispatch(
                            updatePairingEditor(props.id, {
                              plannedCreditMap: {
                                ...plannedCreditMap,
                                [key]: {
                                  ...plannedCreditMap[key],
                                  credit: parseFloat(e.target.value),
                                },
                              },
                            })
                          );
                        }}
                        inputProps={{
                          sx: {
                            padding: "2px 0 2px 5px",
                            fontSize: "12px",
                            zIndex: 2,
                            color: `${theme.custom.forms.inputText} !important`,
                          },
                        }}
                      />
                    </Grid>
                  </Box>
                </Grid>
              ))}
            <Grid item>
              <CreditSummaryCard
                pairingId={props.id}
                pairingDirty={props.pairingDirty}
                onCreditRecalculated={props.onCreditRecalculated}
                plannedRows={plannedCreditRows}
                currentRows={currentCreditRows}
                creditSettingValue={creditSettingValue}
              />
            </Grid>
            <Grid item>
              <AddPairingPositions id={props.id} />
            </Grid>
          </Grid>
          <Grid item style={{ width: "100%" }}>
            <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
              <Box sx={{ textAlign: "end", fontSize: "12px", width: "70px", color: "#ffd351 !important" }}>
                Crew Note
              </Box>
              <TextField
                className="crewnote"
                value={crewNotes}
                multiline={true}
                onChange={(e) => setCrewNotes(e.target.value)}
                onBlur={(e: any) => {
                  dispatch(
                    updatePairingEditor(props.id, { crewNote: { ...storedPairing.crewNote, note: e.target.value } })
                  );
                }}
                minRows={1}
                maxRows={5}
                inputProps={{
                  sx: {
                    padding: "2px 0 2px 5px",
                    fontSize: "12px",
                    fontWeight: 500,
                    zIndex: 2,
                    color: "#000 !important",
                    backgroundColor: "#ffd351",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};
